import { BriefDeliverableTaskTypeEnum } from "@entities/brief/lib/constants";
import { useCreateScripting } from "@entities/brief/lib/hooks/scripting/useCreateScripting";
import { useUpdateScriptingBasic } from "@entities/brief/lib/hooks/scripting/useUpdateScriptingBasic";
import { useGetBriefDeliverableConfig } from "@entities/brief/lib/hooks/useGetBriefDeliverableConfig";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  countMatchingCreators,
  hasBriefHtml,
  listMatchingDeliverableTasks,
  type AttachTaskCreator,
} from "./attachTask";
import { useAttachEditorContent } from "./useAttachEditorContent";

interface UseAttachScriptModalParams {
  open: boolean;
  selectedCount: number;
  creators: AttachTaskCreator[];
  confirmLoading?: boolean;
  onConfirm: (taskId: string, copyFromScriptId: string) => void;
}

export function useAttachScriptModal({
  open,
  selectedCount,
  creators,
  confirmLoading,
  onConfirm,
}: UseAttachScriptModalParams) {
  const { briefId } = useBriefStore();
  const { config } = useGetBriefDeliverableConfig(briefId || "");
  const { createScripting, isCreating, isCreated, isError, data } =
    useCreateScripting();
  const editorRef = useRef<EditorHandle>(null);
  const pendingAttachRef = useRef(false);
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;
  const [step, setStep] = useState<1 | 2>(1);
  const [taskId, setTaskId] = useState<string>();
  const [scriptingId, setScriptingId] = useState<string>();
  const [pendingCreate, setPendingCreate] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const {
    hasContent,
    setHasContent,
    isMediaUploading,
    resetContentState,
    syncContent,
    handleImportComplete,
    handleMediaUploadStart,
    handleMediaUploadComplete,
  } = useAttachEditorContent(editorRef);
  const taskIdRef = useRef(taskId);
  const scriptingIdRef = useRef(scriptingId);
  taskIdRef.current = taskId;
  scriptingIdRef.current = scriptingId;

  const { updateBasic, isUpdating } = useUpdateScriptingBasic(
    scriptingId || "",
    {
      onSuccess: () => {
        if (
          !pendingAttachRef.current ||
          !taskIdRef.current ||
          !scriptingIdRef.current
        ) {
          return;
        }
        pendingAttachRef.current = false;
        onConfirmRef.current(taskIdRef.current, scriptingIdRef.current);
      },
    }
  );

  const deliverableOptions = useMemo(
    () =>
      listMatchingDeliverableTasks(config, BriefDeliverableTaskTypeEnum.Script),
    [config]
  );

  const selectedDeliverable = deliverableOptions.find(
    (option) => option.taskId === taskId
  );
  const canMeasureSkip =
    creators.length > 0 && creators.length === selectedCount;
  const loadedMatchingCount = selectedDeliverable
    ? countMatchingCreators(
        creators,
        selectedDeliverable.deliverableConfigRefId
      )
    : 0;
  const skippedCount = canMeasureSkip
    ? Math.max(0, creators.length - loadedMatchingCount)
    : 0;

  useEffect(() => {
    if (!open) {
      setStep(1);
      setTaskId(undefined);
      setScriptingId(undefined);
      setPendingCreate(false);
      resetContentState();
      pendingAttachRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open || taskId || deliverableOptions.length !== 1) return;
    setTaskId(deliverableOptions[0].taskId);
  }, [open, deliverableOptions, taskId]);

  useEffect(() => {
    if (isError) setPendingCreate(false);
  }, [isError]);

  useEffect(() => {
    const createdId = data?.data?._id;
    if (!pendingCreate || !isCreated || !createdId) return;
    setScriptingId(createdId);
    setPendingCreate(false);
    setHasContent(false);
    setEditorKey((key) => key + 1);
    setStep(2);
  }, [pendingCreate, isCreated, data]);

  const isBusy = Boolean(confirmLoading) || isMediaUploading || isUpdating;
  const canContinue =
    Boolean(taskId) && (!canMeasureSkip || loadedMatchingCount > 0);

  const handleContinue = () => {
    if (!canContinue || !briefId) return;
    if (scriptingId) {
      setStep(2);
      return;
    }
    setPendingCreate(true);
    createScripting(briefId);
  };

  const handleAttach = () => {
    if (!taskId || !scriptingId) return;
    const html = editorRef.current?.getHTML({ syncTokens: true }) ?? "";
    if (!hasBriefHtml(html)) return;
    pendingAttachRef.current = true;
    updateBasic({ scriptId: scriptingId, concept: html, silent: true });
  };

  return {
    step,
    setStep,
    taskId,
    setTaskId,
    scriptingId,
    editorRef,
    editorKey,
    hasContent,
    isBusy,
    isCreating,
    pendingCreate,
    canContinue,
    deliverableOptions,
    selectedDeliverable,
    skippedCount,
    syncContent,
    handleImportComplete,
    handleMediaUploadStart,
    handleMediaUploadComplete,
    handleContinue,
    handleAttach,
  };
}
